import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, Settings, LogOut, MessageCircle, User, Share2, Briefcase } from 'lucide-react';
import { useUserStore } from '../stores/userStore';
import { useNotificationStore } from '../stores/notificationStore';
import Logo from '../assets/images/logo.png';
import NotificationDropdown from './NotificationDropdown';
import { auth } from '../firebase';
import { signOut } from 'firebase/auth';

const DashboardHeader: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user, clearUser } = useUserStore();
    const { unreadCount } = useNotificationStore();

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isNotificationOpen, setIsNotificationOpen] = useState(false);

    const handleLogout = async () => {
        setIsDropdownOpen(false);
        try {
            await signOut(auth);
        } catch (_e) {
            // ignore firebase errors
        }
        clearUser();
        navigate('/sign-in');
    };
    const dropdownRef = useRef<HTMLDivElement>(null);
    const notificationRef = useRef<HTMLDivElement>(null);

    // Helper to get initials
    const getInitials = (firstName?: string, lastName?: string) => {
        return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}` || 'U';
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
            if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
                setIsNotificationOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
            <div className="container mx-auto px-4 sm:px-6 py-3 max-w-7xl">
                <div className="flex items-center justify-between gap-4">
                    <Link to="/dashboard">
                        <img src={Logo} alt="" className='w-[150px]' />
                    </Link>
                    <div className="flex items-center gap-3 ml-auto">
                        <button
                            onClick={() => {
                                const inviteText = t('dashboard_header.invite_text');
                                const inviteUrl = 'https://www.visaconnectus.com';
                                if (navigator.share) {
                                    navigator.share({ title: t('dashboard_header.invite_title'), text: inviteText, url: inviteUrl }).catch(() => {});
                                } else {
                                    navigator.clipboard.writeText(inviteUrl + ' - ' + inviteText).then(() => {
                                        alert(t('dashboard_header.link_copied'));
                                    }).catch(() => {});
                                }
                            }}
                            className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-full transition-colors focus:outline-none"
                            aria-label={t('common.invite_friend')}
                        >
                            <Share2 className="h-5 w-5" />
                        </button>
                        <div className="relative" ref={notificationRef}>
                            <button
                                onClick={() => navigate('/notifications')}
                                className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-full transition-colors focus:outline-none"
                                aria-label={unreadCount > 0 ? t('dashboard_header.notifications_with_count', { count: unreadCount }) : t('dashboard_header.notifications_aria')}
                            >
                                <Bell className="h-5 w-5" />
                                {unreadCount > 0 && (
                                    <span className="absolute top-1 right-1 h-4 w-4 bg-red-500 flex items-center justify-center text-[10px] text-white font-bold rounded-full border-2 border-white">
                                        {unreadCount > 99 ? '99+' : unreadCount}
                                    </span>
                                )}
                            </button>

                            {isNotificationOpen && (
                                <NotificationDropdown onClose={() => setIsNotificationOpen(false)} />
                            )}
                        </div>

                        <div className="relative" ref={dropdownRef}>
                            <button
                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold text-sm hover:ring-2 hover:ring-blue-100 transition-all focus:outline-none"
                            >
                                {user?.profile_photo_url ? (
                                    <img
                                        src={user.profile_photo_url}
                                        alt={t('photo_upload.profile_alt')}
                                        className="h-full w-full rounded-full object-cover"
                                    />
                                ) : (
                                    getInitials(user?.first_name, user?.last_name)
                                )}
                            </button>

                            {isDropdownOpen && (
                                <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-2 animate-in fade-in zoom-in-95 duration-200 origin-top-right">
                                    <div className="px-4 py-2 border-b border-gray-50">
                                        <p className="font-semibold text-gray-900 truncate">{[user?.first_name, user?.last_name].filter(Boolean).join(' ')}</p>
                                        <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                                        <p className="text-xs text-blue-600 font-medium mt-0.5">{user?.visa_type}</p>
                                    </div>

                                    <div className="py-1">
                                        <button
                                            onClick={() => { setIsDropdownOpen(false); navigate(user?.is_employer ? '/employer-profile' : '/public-profile'); }}
                                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                        >
                                            <User className="h-4 w-4" />
                                            {t('dashboard_header.menu_profile')}
                                        </button>
                                        {user?.is_employer && (
                                            <button
                                                onClick={() => { setIsDropdownOpen(false); navigate('/employer-dashboard'); }}
                                                className="w-full text-left px-4 py-2 text-sm text-[#0085FF] hover:bg-blue-50 flex items-center gap-2 font-medium"
                                            >
                                                <Briefcase className="h-4 w-4" />
                                                {t('dashboard_header.menu_employer_dashboard')}
                                            </button>
                                        )}
                                        <button
                                            onClick={() => { setIsDropdownOpen(false); navigate('/chat'); }}
                                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                        >
                                            <MessageCircle className="h-4 w-4" />
                                            {t('dashboard_header.menu_chat')}
                                        </button>
                                        <button
                                            onClick={() => { setIsDropdownOpen(false); navigate('/settings'); }}
                                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                        >
                                            <Settings className="h-4 w-4" />
                                            {t('dashboard_header.menu_settings')}
                                        </button>
                                    </div>

                                    <div className="border-t border-gray-50 py-1">
                                        <button
                                            onClick={handleLogout}
                                            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                        >
                                            <LogOut className="h-4 w-4" />
                                            {t('dashboard_header.menu_logout')}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </header>
    );
};

export default DashboardHeader;
